<template>
	<v-list-item
		:id="asset.AssetID"
		:class="{'disabled-event': expiredKillDateTime}"
		class="asset-container"
		@dblclick.prevent="openAssetManagement"
		@contextmenu.prevent="openContextMenu"
	>
		<div class="d-flex align-center">
			<v-tooltip location="bottom" :text="assetTypeDescription">
				<template #activator="{props}">
					<v-icon
						v-bind="props"
						class="asset-type-icon"
						:icon="assetTypeIcon"
						:color="assetTypeColor"
						size="20"
					/>
				</template>
			</v-tooltip>

			<div class="text-truncate">
				<v-list-item-subtitle v-if="asset.Artist" class="text-caption">
					{{ asset.Artist }} {{ album }} {{ version }}
				</v-list-item-subtitle>
				<v-tooltip location="bottom" :text="titleThirdPartyIDText">
					<template #activator="{props}">
						<v-list-item-title v-bind="props" class="text-h5">
							{{ titleThirdPartyIDText }}
						</v-list-item-title>
					</template>
				</v-tooltip>
			</div>

			<div class="asset-length ml-auto d-flex text-h5 justify-end">
				{{ secondsToFormat(duration, "h:mm:ss", {trim: "left", stopTrim: "m"}) }}
				<div v-if="intro">&nbsp;|&nbsp;{{ secondsToFormat(intro, "m:ss") }}</div>
			</div>

			<v-list-item-action class="asset-actions justify-center">
				<v-menu v-model="contextMenu">
					<template #activator="{props}">
						<v-btn v-bind="props" icon="mdi-dots-vertical" variant="text" density="compact" color="#717277">
						</v-btn>
					</template>
					<v-list>
						<v-list-item
							:disabled="auditionAudioMenu.open"
							@click="openAuditionAudioWidget($event, asset, 'library')"
						>
							<v-list-item-title class="text-body-2"> Audition Audio </v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
			</v-list-item-action>
		</div>
		<AuditionAudioWidget
			v-if="auditionAudioMenu.open"
			ref="auditionAudioWidget"
			:menu="auditionAudioMenu"
			:event="asset"
			parent-component="library"
			@close-menu="closeAuditionAudioWidget"
		/>
	</v-list-item>
</template>

<script>
/* Libraries */
import moment from "moment";

/* Modules */
import AuditionAudioWidget from "@/components/AuditionAudioWidget.vue";

/* Mixins */
import AssetPropertiesMixin from "@/mixins/AssetProperties";
import AuditionAudioMixin from "@/mixins/AuditionAudio";
import DurationFormatMixin from "@/mixins/DurationFormatMixin";

export default {
	name: "LibraryItem",

	components: {
		AuditionAudioWidget
	},

	mixins: [AssetPropertiesMixin, AuditionAudioMixin, DurationFormatMixin],

	props: ["selectedStationID", "asset"],

	data() {
		return {
			contextMenu: false
		};
	},

	computed: {
		album() {
			return this.asset.Album ? ` - ${this.asset.Album}` : "";
		},

		version() {
			return this.asset.Version ? ` - ${this.asset.Version}` : "";
		},

		thirdPartyID() {
			return this.asset.ThirdPartyIDs?.ExternalID ?? this.asset.ThirdPartyIDs?.MusicPointID ?? null;
		},

		titleThirdPartyIDText() {
			return this.asset.Title + (this.thirdPartyID ? ` - ${this.thirdPartyID}` : "");
		},

		expiredKillDateTime() {
			return moment().isAfter(this.asset?.KillDateTime);
		}
	},

	created() {
		// Close other open context menus on right click
		window.addEventListener("contextmenu", this.closeContextMenu);
	},

	beforeUnmount() {
		window.removeEventListener("contextmenu", this.closeContextMenu);
	},

	mounted() {
		let assetTypeColor = this.assetTypeColor ?? "rgb(255, 255, 255)";

		this.$el.style.setProperty("--assetTypeColorRGBValues", assetTypeColor.match(/\d+/g).join(","));
	},

	methods: {
		openContextMenu() {
			this.contextMenu = !this.contextMenu;
		},

		openAssetManagement() {
			const unreleasedAsset = moment(this.asset.ReleaseDate).isAfter(moment());
			const accessUnreleasedAsset = this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.selectedStationID,
				reference: "zettacloud_asset_access_unreleased_type",
				asset_type_id: this.asset.AssetTypeID
			});

			const assetViewByAssetType = this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.selectedStationID,
				reference: "zettacloud_asset_view_bytype",
				asset_type_id: this.asset.AssetTypeID
			});

			if (!unreleasedAsset || (accessUnreleasedAsset && assetViewByAssetType)) {
				this.$store.dispatch("getAsset", this.asset.AssetID).then(asset => {
					this.$store.commit("setAssetManagementStationID", this.selectedStationID);
					this.$store.commit("setAssetManagementAsset", asset);
					this.$store.commit("setAssetManagementIsOpen", true);
				});
			} else {
				this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
					message: "You don't have access to access unreleased assets!"
				});
			}
		},

		closeContextMenu(ev) {
			if (!this.$el.contains(ev.target)) {
				this.contextMenu = false;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.asset-container {
	border-left: 8px solid rgb(var(--assetTypeColorRGBValues));
	box-shadow: 0 0 0 1px $grey-200;
	height: 48px;
	padding: 0 0 0 8px !important;

	.asset-type-icon {
		min-width: 30px;
	}

	.asset-length {
		min-width: 60px;
	}

	.asset-actions {
		min-width: 50px;

		.v-btn {
			min-width: 30px;
		}
	}

	&.sortable-ghost {
		background: rgba(var(--assetTypeColorRGBValues), 0.17);

		.v-list-item-title,
		.v-list-item__content,
		.v-list-item__action * {
			color: $grey-400 !important;
		}
	}

	&.disabled-event {
		background: rgba(113, 114, 119, 0.12);

		.v-list-item__content,
		.v-list-item-title {
			color: $grey-400;
		}
	}
}
</style>
